// Client-side technical indicators computed from candle data.
export function computeRSI(candles, period = 14) {
  if (!candles || candles.length < period + 1) return [];
  const out = [];
  let gain = 0, loss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    if (diff >= 0) gain += diff; else loss -= diff;
  }
  let avgGain = gain / period;
  let avgLoss = loss / period;
  out.push({ time: candles[period].time, value: 100 - 100 / (1 + (avgGain / (avgLoss || 1e-9))) });
  for (let i = period + 1; i < candles.length; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    const g = diff > 0 ? diff : 0;
    const l = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + g) / period;
    avgLoss = (avgLoss * (period - 1) + l) / period;
    const rs = avgGain / (avgLoss || 1e-9);
    out.push({ time: candles[i].time, value: 100 - 100 / (1 + rs) });
  }
  return out;
}

function sma(values, period) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period - 1) {
      out[i] = sum / period;
      sum -= values[i - period + 1];
    }
  }
  return out;
}

function ema(values, period) {
  const out = new Array(values.length).fill(null);
  const k = 2 / (period + 1);
  let prev = null;
  for (let i = 0; i < values.length; i++) {
    prev = prev == null ? values[i] : values[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

export function computeSMA(candles, period = 20) {
  if (!candles || candles.length < period) return [];
  const closes = candles.map((c) => c.close);
  const smaLine = sma(closes, period);
  return candles.map((c, i) => ({ time: c.time, value: smaLine[i] }));
}

export function computeEMA(candles, period = 20) {
  if (!candles || candles.length < period) return [];
  const closes = candles.map((c) => c.close);
  const emaLine = ema(closes, period);
  return candles.map((c, i) => ({ time: c.time, value: emaLine[i] }));
}

export function computeBollingerBands(candles, period = 20, stdDev = 2) {
  if (!candles || candles.length < period) return { middle: [], upper: [], lower: [] };
  const closes = candles.map((c) => c.close);
  const middle = sma(closes, period);
  const upper = new Array(candles.length).fill(null);
  const lower = new Array(candles.length).fill(null);
  
  for (let i = period - 1; i < candles.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sum += Math.pow(closes[j] - middle[i], 2);
    }
    const std = Math.sqrt(sum / period);
    upper[i] = middle[i] + stdDev * std;
    lower[i] = middle[i] - stdDev * std;
  }
  
  return {
    middle: candles.map((c, i) => ({ time: c.time, value: middle[i] })),
    upper: candles.map((c, i) => ({ time: c.time, value: upper[i] })),
    lower: candles.map((c, i) => ({ time: c.time, value: lower[i] })),
  };
}

export function computeMACD(candles, fast = 12, slow = 26, signal = 9) {
  if (!candles || candles.length < slow + signal) return { macd: [], signal: [], hist: [] };
  const closes = candles.map((c) => c.close);
  const emaFast = ema(closes, fast);
  const emaSlow = ema(closes, slow);
  const macdLine = closes.map((_, i) => emaFast[i] - emaSlow[i]);
  const signalLine = ema(macdLine, signal);
  return {
    macd: candles.map((c, i) => ({ time: c.time, value: macdLine[i] })),
    signal: candles.map((c, i) => ({ time: c.time, value: signalLine[i] })),
    hist: candles.map((c, i) => ({
      time: c.time,
      value: macdLine[i] - signalLine[i],
      color: (macdLine[i] - signalLine[i]) >= 0 ? "#00E676" : "#FF3B30",
    })),
  };
}
